import type { TFunction } from "i18next";
import type {
  OutOfRangeAction,
  QualityFrequencyBasis,
  QualityPhase,
  QualityResultType,
} from "../../../api/quality";

/**
 * Screen words for the configurator's enumerations.
 *
 * Kept in one place because the same labels appear on the catalogue, on the plan, and later on the
 * entry forms — and because a raw `FINISHED` or `PER_PALLET` on a Serbian screen is the bug this file
 * exists to prevent (the same one that put „EXPIRED" on the contracts list).
 */
export function phaseLabel(t: TFunction, phase: QualityPhase): string {
  switch (phase) {
    case "INTAKE":
      return t("quality.phase.INTAKE", "Prijem, pre vaganja");
    case "PROCESS":
      return t("quality.phase.PROCESS", "Proces proizvodnje");
    case "SEMI_FINISHED":
      return t("quality.phase.SEMI_FINISHED", "Poluproizvod");
    case "FINISHED":
      return t("quality.phase.FINISHED", "Gotov proizvod");
    case "DISPATCH":
      return t("quality.phase.DISPATCH", "Otprema");
  }
}

/** One line on what each phase is, shown under its heading. */
export function phaseHint(t: TFunction, phase: QualityPhase): string {
  switch (phase) {
    case "INTAKE":
      return t("quality.phaseHint.INTAKE", "Meri se na kamionu pre vage; GMO oko 10 minuta, ceo uzorak oko 30.");
    case "PROCESS":
      return t("quality.phaseHint.PROCESS", "Unose vođe smena, kontroliše tehnolog proizvodnje.");
    case "SEMI_FINISHED":
      return t("quality.phaseHint.SEMI_FINISHED", "Meri se na poluproizvodu, pre nego što uđe u gotov proizvod.");
    case "FINISHED":
      return t("quality.phaseHint.FINISHED", "Hemija po lotu, funkcionalna svojstva po paleti, mikrobiologija više puta po lotu.");
    case "DISPATCH":
      return t("quality.phaseHint.DISPATCH", "Provera pri utovaru, pre nego što roba izađe.");
  }
}

export function resultTypeLabel(t: TFunction, type: QualityResultType): string {
  switch (type) {
    case "NUMERIC":
      return t("quality.resultType.NUMERIC", "Brojčana vrednost");
    case "BOOLEAN":
      return t("quality.resultType.BOOLEAN", "Da ili ne");
    case "TEXT":
      return t("quality.resultType.TEXT", "Opis");
  }
}

export function frequencyLabel(t: TFunction, basis: QualityFrequencyBasis, count: number): string {
  const n = Number(count);
  switch (basis) {
    case "PER_TRUCK":
      return t("quality.freq.PER_TRUCK", "{{n}} po kamionu", { n });
    case "PER_LOT":
      return t("quality.freq.PER_LOT", "{{n}} po lotu", { n });
    case "PER_PALLET":
      // Half a test per pallet is how „u svakom drugom uzorku palete" is written as a number; the
      // screen says it in words, because 0,5 analiza po paleti reads as an error.
      return n === 0.5
        ? t("quality.freq.everySecondPallet", "na svaku drugu paletu")
        : t("quality.freq.PER_PALLET", "{{n}} po paleti", { n });
    case "PER_BATCH":
      return t("quality.freq.PER_BATCH", "{{n}} po šarži", { n });
    case "DAILY":
      return n === 1 ? t("quality.freq.daily", "svakog dana") : t("quality.freq.DAILY", "{{n}} dnevno", { n });
    case "EVERY_N_MONTHS":
      return t("quality.freq.EVERY_N_MONTHS", "jednom u {{n}} meseci", { n });
  }
}

export function outOfRangeLabel(t: TFunction, action: OutOfRangeAction | null): string {
  switch (action) {
    case "REFUSE":
      return t("quality.action.REFUSE", "Kamion se vraća");
    case "SETTLE_WITH_PENALTY":
      return t("quality.action.SETTLE_WITH_PENALTY", "Prima se sa umanjenjem");
    case "APPROVAL_REQUIRED":
      return t("quality.action.APPROVAL_REQUIRED", "Prima se uz odobrenje");
    case "HOLD_LOT":
      return t("quality.action.HOLD_LOT", "Lot se ne pušta za otpremu");
    default:
      return t("quality.action.none", "Nije određeno");
  }
}
